import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { ChipSelectList } from '@marageti/z4-lib';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';
import ReminderSummaryItem from './reminder-summary-item';
import SummaryCard from '../summary-card';

type StatusFilter = 'open' | 'completed';

const STATUS_FILTER_OPTIONS = [
  { key: 'open', label: 'Open', value: 'open' },
  { key: 'completed', label: 'Completed', value: 'completed' },
];

const ITEMS_PER_PAGE = 10;

type RemindersSummaryViewProps = {
  reminders: Ticket[];
  isLoading: boolean;
  togglingCompleteId?: string;
  showYear?: boolean;
  showFilters?: boolean;
  emptyMessage?: string;
  onAddReminder: () => void;
  onEditReminder: (reminder: Ticket) => void;
  onDuplicateReminder: (reminder: Ticket) => void;
  onRescheduleReminder: (reminder: Ticket) => void;
  onToggleComplete: (reminder: Ticket) => void;
  onDismissReminder: (reminder: Ticket) => void;
};

const RemindersSummaryView = ({
  reminders,
  isLoading,
  togglingCompleteId,
  showYear = false,
  showFilters = false,
  emptyMessage = 'There are no reminders for this trip.',
  onAddReminder,
  onEditReminder,
  onDuplicateReminder,
  onRescheduleReminder,
  onToggleComplete,
  onDismissReminder,
}: RemindersSummaryViewProps) => {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [selectedFilters, setSelectedFilters] = useState<StatusFilter[]>(['open', 'completed']);

  const handleFilterChange = (chips: string[]) => {
    if (chips.length === 0) {
      setSelectedFilters(['open', 'completed']);
      return;
    }
    setSelectedFilters(chips as StatusFilter[]);
  };

  const filteredReminders = useMemo(() => {
    if (!showFilters || selectedFilters.length === 2) {
      return reminders;
    }
    return reminders.filter(({ status }) => (selectedFilters.includes('open') && status === 'PENDING')
    || (selectedFilters.includes('completed') && status === 'COMPLETED'));
  }, [reminders, selectedFilters, showFilters]);

  const visibleReminders = filteredReminders.slice(0, visibleCount);
  const hasMore = filteredReminders.length > visibleCount;

  const handleSeeMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  return (
    <SummaryCard
      title="Reminders"
      isLoading={isLoading}
      isEmpty={filteredReminders.length === 0}
      emptyMessage={emptyMessage}
      hasMore={hasMore}
      onAdd={onAddReminder}
      onSeeMore={handleSeeMore}
      headerContent={showFilters ? (
        <Box>
          <ChipSelectList
            chipOptions={STATUS_FILTER_OPTIONS}
            selectedChips={selectedFilters}
            onChange={handleFilterChange}
          />
        </Box>
      ) : undefined}
    >
      {visibleReminders.map((reminder) => (
        <ReminderSummaryItem
          key={reminder.id}
          reminder={reminder}
          isTogglingComplete={togglingCompleteId === reminder.id}
          showYear={showYear}
          onReschedule={() => onRescheduleReminder(reminder)}
          onEdit={() => onEditReminder(reminder)}
          onDuplicate={() => onDuplicateReminder(reminder)}
          onDismiss={() => onDismissReminder(reminder)}
          onToggleComplete={() => onToggleComplete(reminder)}
        />
      ))}
    </SummaryCard>
  );
};

export default RemindersSummaryView;
